import { useState } from 'react';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { useCabecera } from '../../marco/cabecera';
import { ICONO_AJUSTES, iniciales } from './reglas';

/**
 * «Llamadas» del Buzón (11.9, Voz IA en centralita, **próximamente**): tal como está dibujado, la cabecera «Próximamente»
 * y debajo un ejemplo con datos inventados (tres llamadas y su transcripción). No hay operaciones de panel para la voz
 * (solo `receiveVoiceProviderEvent`): ni la lista ni los ajustes leen la API. Los ajustes de la voz se enseñan bloqueados.
 */
interface Llamada {
    id: string;
    nombre: string;
    rojo: boolean;
    hora: string;
    icono: NombreIcono;
    chip: string | null;
    chipEstado: EstadoChip;
    sub: string;
    enCurso: boolean;
    estado: string;
    nota: string;
    notaIcono: NombreIcono;
    notaTono: 'rojo' | 'aviso' | 'gris';
    lineas: readonly (readonly ['c' | 'a', string, string])[];
}

const EJEMPLO: readonly Llamada[] = [
    {
        id: 'v1', nombre: 'Ana Martín', rojo: true, hora: 'Ahora', icono: 'phone-forwarded', chip: 'En curso', chipEstado: 'live', sub: 'Te la pasa el asistente · suena en Recepción', enCurso: true,
        estado: 'Teléfono · en curso · 2:14', nota: 'El asistente te pasa la llamada: la clienta quiere hablar con una persona. Está sonando en Recepción.', notaIcono: 'phone-forwarded', notaTono: 'rojo',
        lineas: [['c', '13:08', 'Hola, llamo por la reserva del sábado a nombre de Ana Martín.'], ['a', '13:08', 'Hola, Ana. Te atiende el asistente automático de Caseta Embrujo. Veo tu reserva para 4 el sábado a las 21:00. ¿Qué necesitas?'], ['c', '13:09', 'Quería saber si podemos llevar al perro a la terraza.'], ['a', '13:09', 'Esa pregunta prefiero que te la responda el equipo. Te paso con una persona, un momento.']],
    },
    {
        id: 'v2', nombre: '+34 6•• ••• 120', rojo: false, hora: '12:32', icono: 'phone', chip: 'Recado', chipEstado: 'pending', sub: 'Nadie contestó: el asistente tomó el recado', enCurso: false,
        estado: 'Teléfono · número sin cliente · 1:05', nota: 'Nadie contestó en Recepción. El asistente tomó el recado y lo dejó en el Buzón para que alguien lo llame.', notaIcono: 'triangle-alert', notaTono: 'aviso',
        lineas: [['c', '12:31', 'Buenas, quería saber si tenéis sala para una despedida el 8 de noviembre, unas 20 personas.'], ['a', '12:31', 'Te paso con el equipo para que te den los detalles.'], ['a', '12:32', 'Ahora no pueden atenderte. Les dejo tu recado y te llaman a este número. ¿A nombre de quién?'], ['c', '12:32', 'De Raquel. Gracias.']],
    },
    {
        id: 'v3', nombre: 'Jorge Santos', rojo: false, hora: '11:15', icono: 'sparkles', chip: null, chipEstado: 'confirmed', sub: '2 entradas para el viernes · 3:20', enCurso: false,
        estado: 'Teléfono · atendida por el asistente · 3:20', nota: 'El asistente atendió la llamada de principio a fin y le mandó las entradas por WhatsApp.', notaIcono: 'circle-check', notaTono: 'gris',
        lineas: [['c', '11:12', 'Quiero dos entradas para el concierto del viernes.'], ['a', '11:12', 'Perfecto, Jorge. Son 36 € en total. Te mando el enlace de pago por WhatsApp.'], ['c', '11:14', 'Ya está pagado.'], ['a', '11:15', 'Recibido. Tienes las entradas en WhatsApp. ¡Que lo disfrutes!']],
    },
];

const AYUDA = [
    { icono: 'phone' as const, titulo: 'Voz IA en centralita', texto: 'El mismo asistente del chat atenderá las llamadas a tu número y avisará al empezar de que es un asistente automático. 300 minutos al mes con el bloque Voz IA (requiere Asistente IA).' },
    { icono: 'phone-forwarded' as const, titulo: 'Llamadas pasadas', texto: 'Si el cliente pide una persona, la llamada suena en tu teléfono y aquí ves lo hablado.' },
    { icono: 'file-text' as const, titulo: 'Sin grabación', texto: 'No se graba el audio: solo se guarda la transcripción, con el mismo plazo que el chat.' },
];

export function Llamadas({ movil, configura }: { movil: boolean; configura: boolean }) {
    const [elegida, setElegida] = useState('v1');
    const [ajustes, setAjustes] = useState(false);
    const actual = EJEMPLO.find((l) => l.id === elegida) ?? EJEMPLO[0];

    useCabecera({
        ayuda: AYUDA,
        acciones: configura ? [{ clave: 'ajustes-voz', texto: movil ? 'Ajustes' : 'Ajustes de la voz', icono: ICONO_AJUSTES, secundaria: true, onClick: () => { setAjustes(true); } }] : [],
    });

    return (
        <div className="rv-bz-voz">
            <section className="rv-bz-prox" aria-label="Voz IA en centralita">
                <span className="rv-bz-prox__icono" aria-hidden="true"><Icono nombre="phone" /></span>
                <span>
                    <span className="rv-bz-prox__t"><strong>Voz IA en centralita</strong><Chip estado="neutral">Próximamente</Chip></span>
                    <small>El asistente cogerá el teléfono y te pasará las llamadas que necesiten a una persona. Debajo, un ejemplo con datos inventados.</small>
                </span>
            </section>
            <div className={`rv-bz-split${movil ? ' rv-bz-split--movil' : ''}`}>
                <div className="rv-bz-lista" aria-label="Llamadas de ejemplo">
                    {EJEMPLO.map((l) => (
                        <button key={l.id} type="button" className="rv-bz-fila" aria-current={l.id === actual?.id} onClick={() => { setElegida(l.id); }}>
                            <span className={`rv-bz-voz__baldosa${l.enCurso ? ' rv-bz-voz__baldosa--viva' : ''}`} aria-hidden="true"><Icono nombre={l.icono} /></span>
                            <span className="rv-bz-fila__txt">
                                <span className="rv-bz-fila__l1"><strong>{l.nombre}</strong><small>{l.hora}</small></span>
                                <span className="rv-bz-fila__l2"><span>{l.sub}</span>{l.chip !== null && <Chip estado={l.chipEstado}>{l.chip}</Chip>}</span>
                            </span>
                        </button>
                    ))}
                </div>
                {actual !== undefined && (
                    <section className="rv-bz-hilo" aria-label={`Llamada de ${actual.nombre}`}>
                        <header className="rv-bz-hilo__cab">
                            <span className={`rv-bz-av${actual.rojo ? ' rv-bz-av--rojo' : ''}`} aria-hidden="true">{iniciales(actual.nombre)}</span>
                            <span className="rv-bz-hilo__quien">
                                <strong>{actual.nombre}</strong>
                                <small className={actual.enCurso ? 'rv-bz-rojo' : undefined}><Icono nombre="phone" />{actual.estado}</small>
                            </span>
                        </header>
                        <div className="rv-bz-hilo__msgs">
                            <p className="rv-bz-sys"><Icono nombre="sparkles" /><span>{`Empezó avisando de que es un asistente automático · ${actual.lineas[0]?.[1] ?? ''}`}</span></p>
                            <p className={`rv-bz-sys rv-bz-sys--${actual.notaTono}`}><Icono nombre={actual.notaIcono} /><span>{actual.nota}</span></p>
                            {actual.lineas.map(([quien, hora, texto], i) => {
                                const grupo = i > 0 && actual.lineas[i - 1]?.[0] === quien;
                                return (
                                    <div key={`${hora}-${String(i)}`} className={`rv-bz-msg ${quien === 'c' ? 'rv-bz-msg--cliente' : 'rv-bz-msg--auto'}${grupo ? ' rv-bz-msg--grupo' : ''}`}>
                                        {quien === 'a' && !grupo && <span className="rv-bz-msg__quien"><Icono nombre="sparkles" />Asistente</span>}
                                        <div className="rv-bz-burbuja"><span>{texto}</span><span className="rv-bz-burbuja__meta">{hora}</span></div>
                                    </div>
                                );
                            })}
                            {actual.enCurso && <p className="rv-bz-transcribiendo"><Icono nombre="mic" />Transcribiendo</p>}
                        </div>
                        <p className="rv-bz-voz__pie"><Icono nombre="lock" /><span>Se atiende en el teléfono del local. No se graba el audio: solo esta transcripción.</span></p>
                    </section>
                )}
            </div>
            <PanelLateral abierto={ajustes} onCerrar={() => { setAjustes(false); }} movil={movil} titulo="Ajustes de la voz" eyebrow="Voz IA en centralita" icono="phone" chip="Próximamente">
                <p className="rv-bz-nota"><Icono nombre="hourglass" /><span>Así serán los ajustes. Se podrán cambiar cuando la Voz IA esté disponible.</span></p>
                <div className="rv-ca-bloque">
                    <Desplegable etiqueta="Número que atiende" valor="+34 951 204 330" opciones={[{ valor: '+34 951 204 330', texto: '+34 951 204 330' }]} onChange={() => undefined} bloqueado />
                    <Desplegable
                        etiqueta="Voz"
                        valor="f"
                        opciones={[{ valor: 'f', texto: 'Femenina · español de España' }, { valor: 'm', texto: 'Masculina · español de España' }]}
                        onChange={() => undefined}
                        bloqueado
                    />
                    <Campo etiqueta="Saludo" valor="Hola, has llamado a Caseta Embrujo. Te atiende el asistente automático: ¿en qué te puedo ayudar?" onChange={() => undefined} filas={3} bloqueado ayuda="Siempre dice que es un asistente automático." />
                    <Campo etiqueta="Pasar las llamadas a" tipo="tel" valor="+34 951 204 331 · Recepción" onChange={() => undefined} bloqueado />
                </div>
                <div className="rv-ca-bloque">
                    <Interruptor etiqueta="Avisar de que es un asistente automático" ayuda="Al empezar cada llamada." valor bloqueado linea="arriba" />
                    <Interruptor etiqueta="Grabar el audio" ayuda="No se graba: solo se guarda la transcripción." valor={false} bloqueado />
                    <Interruptor etiqueta="Si nadie contesta, tomar el recado" ayuda="Queda en el Buzón con la transcripción." valor bloqueado />
                </div>
                <p className="rv-bz-dato"><span>Minutos al mes</span><strong>300 con el bloque Voz IA</strong></p>
            </PanelLateral>
        </div>
    );
}
